import { Ticket, Lock, CalendarClock, CheckCheck, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

import type { TicketTierDto } from '../api/tickets.api';
import { optiuniPremiu } from '../utils/optiuni-premiu';
import { arataNotaRevendicare, etichetaNivel, puncteLipsaNivel } from '../utils/nivel-premiu';
import { tokenBanda, type NivelCuStare } from '../utils/rang';
import { formatZi, type TierState } from '../utils/tier-state';

interface NivelPremiuProps {
    /** Nivelul cu pozitia lui in scara (din `calculeazaRang`, deci din `earned`). */
    nivel: NivelCuStare;
    /** Cate trepte are scara. Decide banda de culoare. */
    total: number;
    earned: number;
    /** Ce poate face scoala ACUM cu nivelul (`computeTierState`: sold, fereastra, limita). */
    state: TierState;
    busy: boolean;
    onRevendica: (tier: TicketTierDto) => void;
}

/**
 * Un rand din lista UNICA de niveluri (item 15). Inlocuieste doua componente care spuneau acelasi
 * lucru de doua ori: scara din `ProgresRang` (pozitia) si grila de `TierCard` (premiul + butonul).
 *
 * Pe rand, de sus in jos: numele si pozitia („Nivelul tău" / „Atins" / „Mai ai N puncte"), costul,
 * optiunile de premiu (o linie din `descriere` = o optiune), apoi fie „Revendică" — activ DOAR cand
 * `computeTierState` spune `available` —, fie nota care explica de ce nu.
 *
 * Pozitia nu se transmite doar prin culoare: banda e `aria-hidden`, langa ea sta mereu un cuvant, iar
 * treptele atinse au si bifa.
 */
export function NivelPremiu({ nivel, total, earned, state, busy, onRevendica }: NivelPremiuProps) {
    const { tier, stare, index } = nivel;
    const disponibil = state.kind === 'available';
    const atins = stare !== 'neatins';
    const optiuni = optiuniPremiu(tier.descriere);

    return (
        <li
            className={
                disponibil
                    ? 'flex min-w-0 overflow-hidden rounded-lg border border-magenta bg-card'
                    : 'flex min-w-0 overflow-hidden rounded-lg border border-hairline bg-card'
            }
        >
            <span
                aria-hidden
                className="w-1.5 shrink-0 self-stretch"
                style={{ backgroundColor: `var(${tokenBanda(index, total)})` }}
            />
            <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">
                <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                        {/* `line-clamp-2`, nu `truncate`: numele le scrie un om si difera adesea
                            abia la coada („Argint - pachet extins…" / „Argint - pachet redus…"). */}
                        <h3 className="m-0 font-semibold line-clamp-2">{tier.name}</h3>
                        <p className="m-0 text-sm text-muted-foreground tabular-nums">
                            {tier.pointsCost} puncte
                            {tier.maxPerSchool != null && ` · ${tier.maxPerSchool} per școală`}
                        </p>
                    </div>
                    <span
                        className={
                            stare === 'curent'
                                ? 'flex shrink-0 items-center gap-1.5 whitespace-nowrap text-small font-semibold'
                                : 'flex shrink-0 items-center gap-1.5 whitespace-nowrap text-small text-muted-foreground'
                        }
                    >
                        {atins
                            ? <Check className="size-4 shrink-0" aria-hidden />
                            : <Lock className="size-4 shrink-0" aria-hidden />}
                        <span className="tabular-nums">{etichetaNivel(stare, puncteLipsaNivel(tier, earned))}</span>
                    </span>
                </div>

                <OptiuniNivel optiuni={optiuni} couponValue={tier.couponValue} />

                {disponibil ? (
                    <Button
                        className="touch-target min-h-12 w-full bg-magenta hover:bg-magenta-hover"
                        disabled={busy}
                        onClick={() => onRevendica(tier)}
                    >
                        <Ticket aria-hidden /> Revendică
                    </Button>
                ) : (
                    arataNotaRevendicare(state, stare) && <NotaRevendicare state={state} />
                )}
            </div>
        </li>
    );
}

/**
 * Optiunile nivelului, exact cum le scrie ECOTIC in `/admin/niveluri-tichete`, o linie = o optiune.
 *
 * Cu mai multe optiuni, un rand de introducere spune ca se alege UNA — altfel lista se citeste ca
 * „primesti toate astea". Fara nicio optiune scrisa, singura informatie despre premiu e valoarea
 * voucherului; nu se inventeaza nimic in plus.
 */
function OptiuniNivel({ optiuni, couponValue }: { optiuni: string[]; couponValue?: number }) {
    if (optiuni.length === 0) {
        return couponValue ? <p className="m-0 text-sm text-dark tabular-nums">Voucher {couponValue} lei</p> : null;
    }

    return (
        <div className="flex flex-col gap-1">
            {optiuni.length > 1 && <p className="m-0 text-xs text-muted-foreground">Alegi una la revendicare:</p>}
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0 text-sm text-dark">
                {optiuni.map((optiune) => (
                    // `optiuniPremiu` scoate duplicatele, deci textul e o cheie stabila.
                    <li key={optiune} className="flex gap-2">
                        <span aria-hidden className="text-magenta">•</span>
                        <span className="min-w-0">{optiune}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function NotaRevendicare({ state }: { state: Exclude<TierState, { kind: 'available' }> }) {
    if (state.kind === 'insufficient') {
        // Ajunge aici doar pe un nivel ATINS (vezi `arataNotaRevendicare`): nivelul e al scolii, dar
        // soldul ramas dupa alte revendicari nu-l mai acopera.
        return (
            <p className="m-0 flex items-center gap-2 text-sm text-muted-foreground">
                <Lock className="size-4 shrink-0" aria-hidden />
                <span className="tabular-nums">Soldul nu ajunge: îți mai trebuie {state.missingPoints} puncte</span>
            </p>
        );
    }

    if (state.kind === 'limit-reached') {
        // Bifa, nu lacat: scoala a folosit tot ce i se cuvenea din nivelul asta. E o realizare.
        return (
            <p className="m-0 flex items-center gap-2 text-sm text-success">
                <CheckCheck className="size-4 shrink-0" aria-hidden />
                <span>Ai atins limita — {state.maxPerSchool} per școală</span>
            </p>
        );
    }

    // outside-window: DATA, nu lacat. `phase` vine din computeTierState; componenta nu recalculeaza.
    const deschidere = state.phase === 'before' ? formatZi(state.availableFrom) : null;
    return (
        <p className="m-0 flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarClock className="size-4 shrink-0" aria-hidden />
            <span>{deschidere ? `Disponibil din ${deschidere}` : 'Perioada s-a încheiat'}</span>
        </p>
    );
}
